import {
  Component,
  computed,
  effect,
  EventEmitter,
  Inject,
  InputSignal,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPageNavModule } from '@vendasta/galaxy/page-nav';
import { GalaxyNavItemComponent } from '@vendasta/galaxy/nav';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../../tokens-and-interfaces';
import { UpdateAssociationFormFieldParams, FormFieldAssociation } from '../interface';
import { GalaxyNavLayoutModule } from '@vendasta/galaxy/nav-layout';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { AssociationFormField } from '../../../tokens-and-interfaces';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { CRMCustomObjectTypeApiService, ListCustomObjectTypesRequestInterface } from '@vendasta/crm';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ListCustomObjectTypesResponse } from '@vendasta/crm/lib/_internal/objects';
import { BehaviorSubject, combineLatest, Observable, of, tap, distinctUntilChanged } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { TranslateByObjectTypeService } from '../../../i18n';
import { MatMenuModule } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';

export interface AssociationFormModalData {
  isMobile: InputSignal<boolean>;
  initialObjectType: ObjectType;
  initialObjectSubtype?: string;
  associationFormFields: AssociationFormField[];
  options: InputSignal<FormFieldAssociation[]>;
  updateAssociation: (associationParams: UpdateAssociationFormFieldParams) => void;
  updateAssociationEventEmitter: EventEmitter<AssociationFormField[]>;
  searchTermChangeEventEmitter: EventEmitter<string | null>;
  objectTypeEmitter: EventEmitter<[ObjectType, string?]>;
}

interface AssociationGroup {
  name: string;
  objectType: ObjectType;
  objectSubtype?: string;
  associations?: FormFieldAssociation[];
}

@Component({
  // requires selector to prevent ID collision
  selector: 'crm-association-form-modal',
  templateUrl: './association-form-modal.component.html',
  styleUrls: ['./association-form-modal.component.scss'],
  imports: [
    GalaxyPageModule,
    GalaxyPageNavModule,
    GalaxyNavItemComponent,
    GalaxyNavLayoutModule,
    GalaxyBadgeModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatButton,
    MatDialogActions,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    AsyncPipe,
    GalaxyPopoverModule,
    MatMenuModule,
    MatDivider,
    MatIconModule,
  ],
})
export class CrmAssociationFormModalComponent {
  protected objectType: WritableSignal<ObjectType>;
  protected objectSubtype: WritableSignal<string | undefined>;
  protected viewAllSelected = signal(true);

  protected searchCtrl = new FormControl('');
  private searchTerm$$ = new BehaviorSubject<string | null>(null);
  protected isLoadingOptions = true;
  private isLoadingSelectedView = true;
  protected isLoadingSelectedView$: Observable<boolean>;

  protected totalAssociations: Signal<number> = computed(() => {
    return this.associationFormFields().reduce((count, field) => count + (field.associations?.length || 0), 0);
  });
  protected associationTabs$: Observable<AssociationGroup[]> = of([]);
  private displayedAssociationFormFields$$: BehaviorSubject<AssociationFormField[]> = new BehaviorSubject<
    AssociationFormField[]
  >([]);
  protected displayedAssociationGroups$: Observable<AssociationGroup[]> = of([]);

  protected isMobile: InputSignal<boolean>;
  private readonly associationFormFields: WritableSignal<AssociationFormField[]>;
  protected options: InputSignal<FormFieldAssociation[]>;
  private readonly updateAssociation: (associationParams: UpdateAssociationFormFieldParams) => void;
  private readonly updateAssociationEventEmitter: EventEmitter<AssociationFormField[]>;
  private readonly searchTermChangeEventEmitter: EventEmitter<string | null>;
  private readonly objectTypeEmitter: EventEmitter<[ObjectType, string?]>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AssociationFormModalData,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private dialogRef: MatDialogRef<CrmAssociationFormModalComponent>,
    private readonly customObjectTypeService: CRMCustomObjectTypeApiService,
    private readonly translateByObjectTypeService: TranslateByObjectTypeService,
    private readonly translateService: TranslateService,
  ) {
    this.isMobile = data.isMobile;
    this.objectType = signal(data.initialObjectType);
    this.objectSubtype = signal(data.initialObjectSubtype);
    this.associationFormFields = signal(data.associationFormFields);
    this.options = data.options;
    this.updateAssociation = data.updateAssociation;
    this.updateAssociationEventEmitter = data.updateAssociationEventEmitter;
    this.searchTermChangeEventEmitter = data.searchTermChangeEventEmitter;
    this.objectTypeEmitter = data.objectTypeEmitter;

    this.handleTabChange(this.objectType(), true, this.objectSubtype());
    this.updateSearchTerm(null);

    this.searchCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((searchTerm: string | null) => this.updateSearchTerm(searchTerm));

    this.associationTabs$ = this.getAssociationTabs();

    this.displayedAssociationGroups$ = combineLatest([
      this.displayedAssociationFormFields$$,
      this.associationTabs$,
      this.searchTerm$$,
    ]).pipe(
      tap(() => (this.isLoadingSelectedView = true)),
      map(
        ([displayedAssociationFormFields, associationTabs, searchTerm]: [
          AssociationFormField[],
          AssociationGroup[],
          string | null,
        ]) => {
          const filteredAssociationFormFields = this.filterAssociationFormFields(
            displayedAssociationFormFields,
            searchTerm,
          );
          return associationTabs.map((associationTab) => {
            let associations =
              filteredAssociationFormFields.find((field) => {
                return field.type === associationTab.objectType;
              })?.associations || [];
            if (associationTab.objectType === 'CustomObject') {
              associations = associations.filter((association) => {
                return association.crmObjectSubtype === associationTab.objectSubtype;
              });
            }
            return {
              ...associationTab,
              associations,
            } as AssociationGroup;
          });
        },
      ),
      tap(() => (this.isLoadingSelectedView = false)),
      shareReplay(),
    );

    this.isLoadingSelectedView$ = this.displayedAssociationGroups$.pipe(map(() => this.isLoadingSelectedView));

    effect(() => {
      this.options();
      this.isLoadingOptions = false;
    });
  }

  private filterAssociationFormFields(
    displayedAssociationFormFields: AssociationFormField[],
    searchTerm: string | null,
  ): AssociationFormField[] {
    return displayedAssociationFormFields.map((field) => {
      return {
        ...field,
        associations: field.associations.filter((association: FormFieldAssociation) => {
          return (
            !searchTerm || !association.name || association.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
          );
        }),
      } as AssociationFormField;
    });
  }

  protected onCheckboxChange(event: MatCheckboxChange, option: FormFieldAssociation, objectType: ObjectType): void {
    if (event.checked) {
      this.handleAddAssociation(option, objectType);
    } else {
      this.handleRemoveAssociation(option, objectType);
    }
  }

  private handleAddAssociation(association: FormFieldAssociation, objectType: ObjectType): void {
    if (!association.crmObjectType || !association.crmObjectId) {
      return;
    }
    this.associationFormFields.update((associationFormFields) =>
      associationFormFields.map((field: AssociationFormField) => {
        if (field.type === objectType) {
          if (!field.associations.find((a) => a.crmObjectId === association.crmObjectId)) {
            return { ...field, associations: [...field.associations, association] };
          }
        }
        return field;
      }),
    );
  }

  private handleRemoveAssociation(association: FormFieldAssociation, objectType: ObjectType): void {
    if (!association.crmObjectType || !association.crmObjectId) {
      return;
    }
    this.associationFormFields.update((associationFormFields) =>
      associationFormFields.map((field: AssociationFormField) => {
        if (field.type === objectType) {
          const updatedAssociations = field.associations.filter((a) => a.crmObjectId !== association.crmObjectId);
          return { ...field, associations: updatedAssociations };
        }
        return field;
      }),
    );
  }

  protected isLastAssociation(association: FormFieldAssociation): boolean {
    if (this.totalAssociations() > 1) {
      return false;
    }
    return !!this.associationFormFields().find((field) => {
      if (field.type === association.crmObjectType) {
        return field.associations.find((a) => a.crmObjectId === association.crmObjectId);
      }
      return false;
    });
  }

  protected handleTabChange(objectType: ObjectType, setViewAllSelected: boolean, objectSubtype?: string): void {
    this.viewAllSelected.set(setViewAllSelected);
    if (setViewAllSelected) {
      this.isLoadingSelectedView = true;
      this.displayedAssociationFormFields$$.next(JSON.parse(JSON.stringify(this.associationFormFields())));
    }
    if (objectType !== this.objectType() || objectSubtype !== this.objectSubtype()) {
      this.isLoadingOptions = true;
      this.objectType.set(objectType);
      this.objectSubtype.set(objectSubtype);
      this.objectTypeEmitter.emit([this.objectType(), this.objectSubtype()]);
    }
  }

  private getAssociationTabs(): Observable<AssociationGroup[]> {
    const associationTabs: AssociationGroup[] = [];
    let customObjectTabs$: Observable<AssociationGroup[]> | null = null;

    for (const association of this.associationFormFields()) {
      if (association.type === 'CustomObject') {
        customObjectTabs$ = this.getCustomObjectTypes().pipe(
          map((response) => {
            if (!response.customObjectTypes) {
              return [];
            }
            return response.customObjectTypes.map((customObjectType) => {
              return {
                name: this.translateService.instant('OBJECT.TITLE_SINGLE', {
                  singularObjectName: customObjectType.singularObjectName,
                }),
                objectType: association.type,
                objectSubtype: customObjectType.customObjectTypeId,
              } as AssociationGroup;
            });
          }),
        );
      } else {
        associationTabs.push({
          name: this.translateByObjectTypeService.getTranslatedObjectType(association.type),
          objectType: association.type,
        } as AssociationGroup);
      }
    }

    if (customObjectTabs$) {
      return customObjectTabs$.pipe(
        take(1),
        map((customObjectTabs) => {
          return [...associationTabs, ...customObjectTabs];
        }),
        shareReplay(1),
      );
    } else {
      return of(associationTabs).pipe(take(1), shareReplay(1));
    }
  }

  getCurrentTabName(): Observable<string> {
    return this.associationTabs$.pipe(
      distinctUntilChanged(),
      map((associationTabs) => {
        if (this.viewAllSelected()) {
          return this.translateService.instant('ACTIONS.SELECTED');
        }
        const currentTab = associationTabs.find(
          (tab) => tab.objectType === this.objectType() && tab.objectSubtype === this.objectSubtype(),
        );
        return currentTab?.name ?? '';
      }),
      shareReplay(1),
    );
  }

  private getCustomObjectTypes(): Observable<ListCustomObjectTypesResponse> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return this.customObjectTypeService.listCustomObjectTypes({
          namespace: namespace,
        } as ListCustomObjectTypesRequestInterface);
      }),
      shareReplay(1),
    );
  }

  protected getGroupTotalAssociations(objectType: ObjectType, objectSubtype?: string) {
    return computed(() => {
      const associations =
        this.associationFormFields().find((field) => {
          return field.type === objectType;
        })?.associations || [];
      if (objectType === 'CustomObject') {
        return associations.filter((association) => {
          return association.crmObjectSubtype === objectSubtype;
        }).length;
      }
      return associations.length;
    });
  }

  private updateSearchTerm(searchTerm: string | null): void {
    this.searchTerm$$.next(searchTerm);
    this.searchTermChangeEventEmitter.emit(searchTerm);
  }

  protected isSelectedAssociation(association: FormFieldAssociation, objectType: ObjectType): boolean {
    return this.associationFormFields().some(
      (field) => field.type === objectType && field.associations.some((a) => a.crmObjectId === association.crmObjectId),
    );
  }

  protected isSelectedTab(objectType: ObjectType, objectSubtype?: string): Signal<boolean> {
    return computed(
      () => !this.viewAllSelected() && this.objectType() === objectType && this.objectSubtype() === objectSubtype,
    );
  }

  protected handleSave(): void {
    this.updateAssociation({
      associationFormFields: this.associationFormFields(),
      actionEventEmitter: this.updateAssociationEventEmitter,
    } as UpdateAssociationFormFieldParams);
    this.dialogRef.close();
  }

  protected handleCancel(): void {
    this.dialogRef.close();
  }
}
